/**
 * Geração da Seção A - METODOLOGIA
 * Contém informações sobre metodologia ISO 12100 e estimativa de riscos HRN
 */

import { IMAGENS_ESTATICAS } from '../static/latex-static-assets';

/**
 * Gera a Seção A - METODOLOGIA completa
 */
export function gerarSecaoMetodologia(): string {
  return `
\\newpage

\\noindent\\section*{Seção A - Procedimentos para Apreciação e Classificação de Riscos}
\\addcontentsline{toc}{section}{A. Procedimentos para Apreciação e Classificação de Riscos}
\\label{sec:metodologia}

${gerarSubsecaoIntroducao()}

${gerarSubsecaoElaboracaoRelatorio()}
${gerarSubsecaoEstimativaRiscos()}
${gerarSubsecaoDeterminacaoCategoria()}
`;
}

/**
 * Gera a subseção A.1 - Introdução
 */
function gerarSubsecaoIntroducao(): string {
  return `
\\subsection*{A.1. Introdução}
\\addcontentsline{toc}{subsection}{A.1. Introdução}

A ArcelorMittal Tubarão, de forma proativa e alinhada às melhores práticas de gestão de Segurança de Máquinas, contratou a empresa Schmersal para conduzir a apreciação de riscos de suas máquinas e equipamentos, com o objetivo de identificar perigos, avaliar criticidade e definir medidas de mitigação capazes de reduzir os riscos a níveis aceitáveis.

Para a elaboração dos relatórios técnicos, a Schmersal adota como referência a norma ISO 12100, que estabelece princípios gerais de projeto seguro e orienta o processo de apreciação e redução de riscos. A metodologia aplicada contempla, inicialmente, a identificação sistemática dos perigos associados às máquinas, considerando seus limites de uso (incluindo uso pretendido e condições de uso razoavelmente previsíveis). Na sequência, procede-se à estimativa dos riscos, mediante a avaliação combinada da probabilidade de ocorrência, da gravidade do possível dano e do nível de exposição ao perigo.

Com base nessa estimativa, os riscos são classificados e são definidas as medidas de redução pertinentes, em conformidade com a hierarquia de controle prevista na ISO 12100, priorizando soluções de eliminação do perigo por projeto, a adoção de proteções e dispositivos de segurança e, quando aplicável, a implementação de informações para uso. Dessa forma, a ArcelorMittal Tubarão reforça seu compromisso com a prevenção de acidentes, a conformidade normativa e a melhoria contínua das condições de segurança em suas operações.

`;
}



/**
 * Gera a subseção A.2 - Elaboração do relatório
 */
function gerarSubsecaoElaboracaoRelatorio(): string {
  return `
\\subsection*{A.2. Elaboração do relatório}
\\addcontentsline{toc}{subsection}{A.2. Elaboração do relatório}

A apreciação de riscos conforme a ISO 12100 consiste em identificar e analisar os perigos associados a uma máquina, iniciando pela definição de seus limites — como uso previsto, usos indevidos razoavelmente previsíveis e condições operacionais. Com os limites definidos, elabora-se a lista de perigos e realiza-se a estimativa de riscos, avaliando a probabilidade e a gravidade de cada situação perigosa. Em seguida, aplica-se o método das três etapas: medidas de segurança inerentes ao projeto, proteções e medidas técnicas complementares e informações de uso, assegurando a redução progressiva e eficaz dos riscos.

A apreciação contempla todas as fases de utilização do equipamento ao longo de seu ciclo de vida, incluindo a Operação Normal, a Limpeza, o Ajuste e a Manutenção, de modo a assegurar que os perigos associados a cada uma dessas etapas sejam devidamente identificados e avaliados.

Nota: Embora a Schmersal utilize a ISO 12100 como referência para a identificação dos perigos, a avaliação é realizada de forma estruturada por partes da máquina, garantindo uma verificação completa. Em cada uma dessas partes, os perigos aplicáveis serão analisados e devidamente identificados.

`;
}


/**
 * Gera a subseção A.3 - Estimativa de Riscos
 */
function gerarSubsecaoEstimativaRiscos(): string {
  return `
\\subsection*{A.3. Estimativa de Riscos}
\\addcontentsline{toc}{subsection}{A.3. Estimativa de Riscos}
\\label{sec:estimativa-riscos}

A Schmersal adota o HRN (Hazard Rating Number), método de fundamento numérico desenvolvido por Chris Steel e publicado em 1990 no periódico Safety and Health Practitioner (SHP), no artigo intitulado "Risk Estimation". Este método permite análises completas e estimativas muito precisas, avaliando as consequências de um evento em termos de danos para as pessoas.

O método HRN é baseado na multiplicação dos seguintes parâmetros definidos por Chris Steel: grau de severidade da lesão (GS), frequência de exposição (FE), probabilidade de ocorrer (PO) e número de pessoas expostas ao risco (NP).

${gerarTabela1GrauSeveridade()}

${gerarTabela2FrequenciaExposicao()}

${gerarTabela3ProbabilidadeOcorrencia()}

${gerarTabela4NumeroPessoas()}

Após a determinação dos fatores acima, deverá ser realizado o cálculo para determinar o valor do HRN que indicará o grau de risco. HRN = GS x FE x PO x NP

${gerarTabela5ClassificacaoRisco()}

Nota: A tabela acima foi definida pela Engenharia da ArcelorMittal, com adequação das faixas de valores em relação ao método HRN original.

Na multiplicação temos duas categorias de HRN:

\\textbf{HRN Atual}: descreve a condição atual máquina;

\\textbf{HRN Projetado}: descreve a situação que será alcançada, considerando a implementação das medidas de redução de risco

`;
}

/**
 * Gera a Tabela 1: Parâmetros do grau de severidade de lesão (GS)
 */
function gerarTabela1GrauSeveridade(): string {
  return `
\\begin{table}[H]
\\centering
\\caption{Parâmetros do grau de severidade de lesão}
\\label{tab:grau-severidade}
\\begin{flushleft}
\\renewcommand{\\arraystretch}{1.5}
\\setlength{\\tabcolsep}{8pt}

\\begin{tabularx}{\\linewidth}{|>{\\raggedright\\arraybackslash}X|>{\\raggedright\\arraybackslash}p{4.5cm}|}
\\hline
\\rowcolor{schmersalBlue}
\\textcolor{white}{\\textbf{Grau de severidade da lesão - GS}} &
\\textcolor{white}{\\textbf{Valor}} \\\\
\\hline
Arranhão / contusão & 0,1 \\\\
\\hline
Laceração / corte / leve problema de saúde & 0,5 \\\\
\\hline
Quebra - ossos pequenos – dedos & 1 \\\\
\\hline
Quebra - ossos grandes – mão / braços / pernas & 2 \\\\
\\hline
Perda de 1 ou 2 dedos & 4 \\\\
\\hline
Amputação de perna / braço, perda parcial da visão audição & 8 \\\\
\\hline
Amputação de 2 pernas / braços, perda total da visão / audição em ambos os olhos / ouvidos & 10 \\\\
\\hline
Doença grave ou permanente & 12 \\\\
\\hline
Fatalidade & 15 \\\\
\\hline
\\end{tabularx}
\\end{flushleft}
\\end{table}
\\vspace{-0.3cm}
\\noindent\\textit{Fonte: STEEL, Chris. Risk Estimation. Safety and Health Practitioner (SHP), v. 8, n. 6, p. 20-21, jun. 1990.}

\\vspace{0.8cm}

`;
}

/**
 * Gera a Tabela 2: Parâmetros da frequência de exposição (FE)
 */
function gerarTabela2FrequenciaExposicao(): string {
  return `
\\begin{table}[H]
\\centering
\\caption{Parâmetros da frequência de exposição}
\\label{tab:frequencia-exposicao}
\\begin{flushleft}
\\renewcommand{\\arraystretch}{1.5}
\\setlength{\\tabcolsep}{8pt}

\\begin{tabularx}{\\linewidth}{|>{\\raggedright\\arraybackslash}X|>{\\raggedright\\arraybackslash}p{4.5cm}|}
\\hline
\\rowcolor{schmersalBlue}
\\textcolor{white}{\\textbf{Frequência de exposição - FE}} &
\\textcolor{white}{\\textbf{Valor}} \\\\
\\hline
Anualmente & 0,5 \\\\
\\hline
Mensalmente & 1 \\\\
\\hline
Semanalmente & 1,5 \\\\
\\hline
Diariamente & 2,5 \\\\
\\hline
Em termos de hora & 4 \\\\
\\hline
Constantemente & 5 \\\\
\\hline
\\end{tabularx}
\\end{flushleft}
\\end{table}
\\vspace{-0.3cm}
\\noindent\\textit{Fonte: STEEL, Chris. Risk Estimation. Safety and Health Practitioner (SHP), v. 8, n. 6, p. 20-21, jun. 1990.}

\\vspace{0.8cm}

`;
}

/**
 * Gera a Tabela 3: Parâmetros da probabilidade de ocorrência (PO)
 */
function gerarTabela3ProbabilidadeOcorrencia(): string {
  return `
\\begin{table}[H]
\\centering
\\caption{Parâmetros da probabilidade de ocorrência}
\\label{tab:probabilidade-ocorrencia}
\\begin{flushleft}
\\renewcommand{\\arraystretch}{1.5}
\\setlength{\\tabcolsep}{8pt}

\\begin{tabularx}{\\linewidth}{|>{\\raggedright\\arraybackslash}X|>{\\raggedright\\arraybackslash}p{4.5cm}|}
\\hline
\\rowcolor{schmersalBlue}
\\textcolor{white}{\\textbf{Probabilidade de ocorrência - PO}} &
\\textcolor{white}{\\textbf{Valor}} \\\\
\\hline
Quase impossível & 0,033 \\\\
\\hline
Muito improvável & 1 \\\\
\\hline
Improvável & 1,5 \\\\
\\hline
Possível & 2 \\\\
\\hline
Plausível & 5 \\\\
\\hline
Provável & 8 \\\\
\\hline
Muito provável & 10 \\\\
\\hline
Quase certeza & 15 \\\\
\\hline
\\end{tabularx}
\\end{flushleft}
\\end{table}
\\vspace{-0.3cm}
\\noindent\\textit{Fonte: STEEL, Chris. Risk Estimation. Safety and Health Practitioner (SHP), v. 8, n. 6, p. 20-21, jun. 1990.}

\\vspace{0.8cm}

`;
}

/**
 * Gera a Tabela 4: Parâmetros do número de pessoas em risco (NP)
 */
function gerarTabela4NumeroPessoas(): string {
  return `
\\begin{table}[H]
\\centering
\\caption{Parâmetros do número de pessoas em risco}
\\label{tab:numero-pessoas}
\\begin{flushleft}
\\renewcommand{\\arraystretch}{1.5}
\\setlength{\\tabcolsep}{8pt}

\\begin{tabularx}{\\linewidth}{|>{\\raggedright\\arraybackslash}X|>{\\raggedright\\arraybackslash}p{4.5cm}|}
\\hline
\\rowcolor{schmersalBlue}
\\textcolor{white}{\\textbf{Número de pessoas em risco - NP}} &
\\textcolor{white}{\\textbf{Valor}} \\\\
\\hline
1 a 2 pessoas & 1 \\\\
\\hline
3 a 7 pessoas & 2 \\\\
\\hline
8 a 15 pessoas & 5 \\\\
\\hline
16 a 50 pessoas & 8 \\\\
\\hline
Mais que 50 pessoas & 12 \\\\
\\hline
\\end{tabularx}
\\end{flushleft}
\\end{table}
\\vspace{-0.3cm}
\\noindent\\textit{Fonte: STEEL, Chris. Risk Estimation. Safety and Health Practitioner (SHP), v. 8, n. 6, p. 20-21, jun. 1990.}

\\vspace{0.8cm}

`;
}

/**
 * Gera a Tabela 5: Classificação do risco
 */
function gerarTabela5ClassificacaoRisco(): string {
  return `
\\begin{table}[H]
\\centering
\\caption{Classificação do risco}
\\label{tab:classificacao-risco}
\\begin{flushleft}
\\renewcommand{\\arraystretch}{1.5}
\\setlength{\\tabcolsep}{8pt}

\\begin{tabularx}{\\linewidth}{|>{\\centering\\arraybackslash}X|>{\\centering\\arraybackslash}X|>{\\centering\\arraybackslash}X|}
\\hline
\\multicolumn{3}{|c|}{\\cellcolor{schmersalBlue}\\textcolor{white}{\\textbf{Classificação - Cliente (Arcelor Mittal)}}} \\\\ \\hline
\\cellcolor{schmersalBlue}\\textcolor{white}{\\textbf{HRN}} & 
\\cellcolor{schmersalBlue}\\textcolor{white}{\\textbf{Risco}} & 
\\cellcolor{schmersalBlue}\\textcolor{white}{\\textbf{Prioridade}} \\\\ \\hline

% --- BLOCO VERDE (Baixa) ---
\\cellcolor{riskGreen} 0 - 1 & \\cellcolor{riskGreen} Aceitável & \\cellcolor{riskGreen} \\\\ \\hhline{|--|~|}
\\cellcolor{riskGreen} 1 - 5 & \\cellcolor{riskGreen} Muito baixo & \\cellcolor{riskGreen} \\multirow{-2}{*}{Baixa} \\\\ \\hline

% --- BLOCO AMARELO (Média) ---
\\cellcolor{riskYellow} 5 - 10 & \\cellcolor{riskYellow} Baixo & \\cellcolor{riskYellow} \\\\ \\hhline{|--|~|}
\\cellcolor{riskYellow} 10 - 50 & \\cellcolor{riskYellow} Significante & \\cellcolor{riskYellow} \\\\ \\hhline{|--|~|}
\\cellcolor{riskYellow} 50 - 100 & \\cellcolor{riskYellow} Alto & \\cellcolor{riskYellow} \\multirow{-3}{*}{Média} \\\\ \\hline

% --- BLOCO VERMELHO (Alta) ---
\\cellcolor{riskRed} 100 - 500 & \\cellcolor{riskRed} Muito alto & \\cellcolor{riskRed} \\\\ \\hhline{|--|~|}
\\cellcolor{riskRed} 500 - 1000 & \\cellcolor{riskRed} Extremo & \\cellcolor{riskRed} \\\\ \\hhline{|--|~|}
\\cellcolor{riskRed} Mais de 1000 & \\cellcolor{riskRed} Inaceitável & \\cellcolor{riskRed} \\multirow{-3}{*}{Alta} \\\\ \\hline

\\end{tabularx}
\\end{flushleft}
\\end{table}
\\vspace{-0.3cm}
\\noindent\\textit{Fonte: COULSON, Derek. Revisiting Risk Estimation (Safety and Health Practitioner) e STEEL, Chris. Risk Estimation (1990).}

\\vspace{0.8cm}
`;
}

/**
 * Gera a subseção A.4 - Determinação da categoria e nível de desempenho
 */
function gerarSubsecaoDeterminacaoCategoria(): string {
  return `
\\subsection*{A.4. Determinação da categoria e nível de desempenho}
\\addcontentsline{toc}{subsection}{A.4. Determinação da categoria e nível de desempenho}
\\label{sec:determinacao-categoria}

Para atingir o nível de desempenho requerido PLr e a categoria deve-se partir do ponto inicial, selecionar o parâmetro de severidade, posteriormente a frequência e finalmente a possibilidade de evitar o dano, obtendo desta maneira um caminho até o PLr ou categoria.

% NOTA: O arquivo ${IMAGENS_ESTATICAS.PLR_CAT} deve estar disponível no diretório de compilação do LaTeX
% O backend deve copiar esta imagem de backend/assets/ ou backend/logo/ para o diretório de compilação
\\vspace{0.8cm}

\\begin{center}
\\includegraphics[width=\\linewidth]{${IMAGENS_ESTATICAS.PLR_CAT}}
\\end{center}

\\vspace{0.8cm}

\\textbf{S Severidade do ferimento}

S1 Ferimento leve (normalmente reversível)

S2 Ferimento sério (normalmente irreversível) incluindo morte

\\vspace{0.5cm}

\\textbf{F Frequência e/ou tempo de exposição ao perigo}

F1 Raro a relativamente frequente e/ou baixo tempo de exposição

F2 Frequente a contínuo e/ou tempo de exposição longo

\\vspace{0.5cm}

\\textbf{P Possibilidade de evitar o perigo}

P1 Possível sob condições específicos

P2 Quase nunca possível

\\vspace{0.5cm}

\\textbf{B, 1 a 4} Categorias para partes relacionadas à segurança de sistemas de comando

\\textcolor{red}{\\Large\\textbullet} Categorias preferenciais para pontos de referência

\\textcolor{red}{\\small\\textbullet} Categorias possíveis que requerem medidas adicionais

\\textcolor{green}{\\Large\\textbullet} Medidas que podem ser superdimensionadas para risco relevante

`;
}

